import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { dpsTheme } from '../../styles/dpsTheme';
import { getAnonymousComparison, ComparisonStats } from '../../services/comparisonService';
import { TrendingUp, TrendingDown, Minus, Users, Home, Building, Lock } from 'lucide-react';
import { useDeviceSize } from '../../hooks/useMediaQuery';
import { MIN_DIAGNOSTICS_FOR_COMPARISON } from '../../config/constants';

interface ComparisonCardProps {
  userScore: number;
  housingType: 'house' | 'apartment';
  showDetailedMessage?: boolean;
}

export const ComparisonCard: React.FC<ComparisonCardProps> = ({
  userScore,
  housingType,
  showDetailedMessage = false
}) => {
  const { isMobile } = useDeviceSize();
  const [stats, setStats] = useState<ComparisonStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadComparison = async () => {
      try {
        setLoading(true);
        setError(false);
        const data = await getAnonymousComparison(userScore, housingType);
        setStats(data);
      } catch (err) {
        console.error('Error loading comparison:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadComparison();
  }, [userScore, housingType]);

  if (loading) {
    return null;
  }

  if (error || !stats) {
    return null;
  }

  if (stats.totalDiagnostics < MIN_DIAGNOSTICS_FOR_COMPARISON) {
    return null;
  }

  const scoreDiff = userScore - stats.averageScore;
  const housingTypeAverage = housingType === 'house'
    ? stats.averageByHousingType.house
    : stats.averageByHousingType.apartment;
  const housingTypeDiff = userScore - housingTypeAverage;

  const getPercentileMessage = () => {
    if (stats.userPercentile >= 90) {
      return "Excellent ! Vous faites partie des logements les mieux sécurisés";
    } else if (stats.userPercentile >= 75) {
      return "Très bien, votre sécurité est supérieure à la moyenne";
    } else if (stats.userPercentile >= 50) {
      return "Votre niveau de sécurité est dans la moyenne";
    } else if (stats.userPercentile >= 25) {
      return "Votre sécurité est inférieure à la moyenne";
    } else {
      return "Attention : votre niveau de sécurité nécessite des améliorations";
    }
  };

  const getComparisonColor = (diff: number) => {
    if (diff >= 10) return dpsTheme.colors.scoreLevel.excellent.text;
    if (diff >= 0) return dpsTheme.colors.scoreLevel.good.text;
    if (diff >= -10) return dpsTheme.colors.scoreLevel.moderate.text;
    return dpsTheme.colors.scoreLevel.weak.text;
  };

  const getComparisonIcon = (diff: number) => {
    if (diff > 5) return <TrendingUp size={20} color={getComparisonColor(diff)} />;
    if (diff < -5) return <TrendingDown size={20} color={getComparisonColor(diff)} />;
    return <Minus size={20} color={getComparisonColor(diff)} />;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        background: dpsTheme.colors.background.card,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${dpsTheme.colors.border.primary}`,
        borderRadius: dpsTheme.borderRadius.lg,
        padding: dpsTheme.spacing['2xl']
      }}
    >
      <div style={{ marginBottom: dpsTheme.spacing.xl }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <Users size={24} color={dpsTheme.colors.accent.blue.primary} />
          <h3 style={{
            fontSize: dpsTheme.typography.fontSize.xl,
            fontWeight: dpsTheme.typography.fontWeight.semibold,
            color: dpsTheme.colors.text.primary,
            margin: 0
          }}>
            Comparaison anonyme
          </h3>
        </div>
        <p style={{
          fontSize: dpsTheme.typography.fontSize.sm,
          color: dpsTheme.colors.text.muted,
          margin: 0
        }}>
          Votre score comparé aux autres utilisateurs de l'application
        </p>
      </div>

      {/* Message principal avec contexte */}
      {showDetailedMessage && stats.userPercentile < 50 && (
        <div style={{
          marginBottom: dpsTheme.spacing.lg,
          padding: dpsTheme.spacing.lg,
          background: 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${dpsTheme.colors.scoreLevel.weak.border}`,
          borderRadius: dpsTheme.borderRadius.md
        }}>
          <div style={{
            fontSize: dpsTheme.typography.fontSize.base,
            color: dpsTheme.colors.text.secondary,
            lineHeight: dpsTheme.typography.lineHeight.relaxed
          }}>
            <strong style={{ color: dpsTheme.colors.scoreLevel.weak.text }}>
              Attention :
            </strong> Votre sécurité est inférieure à {100 - stats.userPercentile}% des logements. 
            Le rapport détaillé vous permettra d'identifier précisément les améliorations prioritaires 
            pour atteindre un niveau de protection optimal.
          </div>
        </div>
      )}

      {/* Message sans contexte premium */}
      {!showDetailedMessage && stats.userPercentile < 50 && (
        <div style={{
          marginBottom: dpsTheme.spacing.lg,
          padding: dpsTheme.spacing.lg,
          background: 'rgba(239, 68, 68, 0.1)',
          border: `1px solid ${dpsTheme.colors.scoreLevel.weak.border}`,
          borderRadius: dpsTheme.borderRadius.md
        }}>
          <div style={{
            fontSize: dpsTheme.typography.fontSize.base,
            color: dpsTheme.colors.text.secondary,
            lineHeight: dpsTheme.typography.lineHeight.relaxed
          }}>
            <strong style={{ color: dpsTheme.colors.scoreLevel.weak.text }}>
              {getPercentileMessage()}
            </strong>
          </div>
        </div>
      )}

      {/* Message positif si score > moyenne */}
      {stats.userPercentile >= 50 && (
        <div style={{
          marginBottom: dpsTheme.spacing.lg,
          padding: dpsTheme.spacing.lg,
          background: 'rgba(16, 185, 129, 0.1)',
          border: `1px solid ${dpsTheme.colors.scoreLevel.excellent.border}`,
          borderRadius: dpsTheme.borderRadius.md
        }}>
          <div style={{
            fontSize: dpsTheme.typography.fontSize.base,
            color: dpsTheme.colors.text.secondary,
            lineHeight: dpsTheme.typography.lineHeight.relaxed
          }}>
            <strong style={{ color: dpsTheme.colors.scoreLevel.excellent.text }}>
              {getPercentileMessage()}
            </strong>
          </div>
        </div>
      )}

      {/* Comparaisons sur 2 colonnes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: dpsTheme.spacing.lg,
        marginBottom: dpsTheme.spacing.lg
      }}>
        {/* Moyenne globale */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: `1px solid ${dpsTheme.colors.border.secondary}`,
          borderRadius: dpsTheme.borderRadius.md,
          padding: isMobile ? dpsTheme.spacing.md : dpsTheme.spacing.lg
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <Users size={16} color={dpsTheme.colors.text.muted} style={{ flexShrink: 0 }} />
              <span style={{
                fontSize: isMobile ? dpsTheme.typography.fontSize.xs : dpsTheme.typography.fontSize.sm,
                color: dpsTheme.colors.text.muted
              }}>
                Moyenne globale
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {getComparisonIcon(scoreDiff)}
              <span style={{
                fontSize: dpsTheme.typography.fontSize.lg,
                fontWeight: dpsTheme.typography.fontWeight.bold,
                color: dpsTheme.colors.text.primary
              }}>
                {stats.averageScore}
              </span>
            </div>
          </div>
          <div style={{
            fontSize: isMobile ? dpsTheme.typography.fontSize.xs : dpsTheme.typography.fontSize.sm,
            color: getComparisonColor(scoreDiff),
            fontWeight: dpsTheme.typography.fontWeight.medium
          }}>
            Vous êtes à {scoreDiff > 0 ? '+' : ''}{scoreDiff} points de la moyenne
          </div>
        </div>

        {/* Moyenne par type de logement */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: `1px solid ${dpsTheme.colors.border.secondary}`,
          borderRadius: dpsTheme.borderRadius.md,
          padding: isMobile ? dpsTheme.spacing.md : dpsTheme.spacing.lg
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              {housingType === 'house' ? (
                <Home size={16} color={dpsTheme.colors.text.muted} style={{ flexShrink: 0 }} />
              ) : (
                <Building size={16} color={dpsTheme.colors.text.muted} style={{ flexShrink: 0 }} />
              )}
              <span style={{
                fontSize: isMobile ? dpsTheme.typography.fontSize.xs : dpsTheme.typography.fontSize.sm,
                color: dpsTheme.colors.text.muted
              }}>
                Moyenne {housingType === 'house' ? 'maisons' : 'appartements'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {getComparisonIcon(housingTypeDiff)}
              <span style={{
                fontSize: dpsTheme.typography.fontSize.lg,
                fontWeight: dpsTheme.typography.fontWeight.bold,
                color: dpsTheme.colors.text.primary
              }}>
                {housingTypeAverage}
              </span>
            </div>
          </div>
          <div style={{
            fontSize: isMobile ? dpsTheme.typography.fontSize.xs : dpsTheme.typography.fontSize.sm,
            color: getComparisonColor(housingTypeDiff),
            fontWeight: dpsTheme.typography.fontWeight.medium
          }}>
            Vous êtes à {housingTypeDiff > 0 ? '+' : ''}{housingTypeDiff} points de la moyenne
          </div>
        </div>
      </div>

      {/* Note de confidentialité */}
      <div style={{
        padding: dpsTheme.spacing.md,
        background: 'rgba(139, 92, 246, 0.1)',
        border: `1px solid rgba(139, 92, 246, 0.2)`,
        borderRadius: dpsTheme.borderRadius.sm,
        fontSize: dpsTheme.typography.fontSize.xs,
        color: dpsTheme.colors.text.disabled,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}>
        <Lock size={14} />
        Comparaison 100% anonyme • Aucune donnée personnelle partagée
      </div>
    </motion.div>
  );
};